import { useMemo, useState } from 'react'
import { Check, Copy, FileJson } from 'lucide-react'
import Modal from '../Modal'
import { BENEFIT_FIELDS } from '../../data/rulesEngineDefaults'
import {
  deriveBenefitInputsFromCheck,
  resolveEligibilityRawFromCheck,
} from '../../services/patientBenefitInputs'
import type { ApiEligibilityCheck } from '../../services/eligibilityChecksService'
import { humanizeFieldKey } from '../../utils/rulesEngineFields'

type PatientEligibilityDataModalProps = {
  open: boolean
  onClose: () => void
  eligibilityCheck: ApiEligibilityCheck | null
  patientName?: string
}

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value ?? '')
  }
}

export default function PatientEligibilityDataModal({
  open,
  onClose,
  eligibilityCheck,
  patientName,
}: PatientEligibilityDataModalProps) {
  const [tab, setTab] = useState<'summary' | 'raw'>('summary')
  const [copied, setCopied] = useState(false)

  const mapped = useMemo(() => {
    if (!eligibilityCheck) return null
    try {
      return deriveBenefitInputsFromCheck(eligibilityCheck)
    } catch {
      return null
    }
  }, [eligibilityCheck])

  const rawBody = useMemo(
    () => (eligibilityCheck ? resolveEligibilityRawFromCheck(eligibilityCheck) : {}),
    [eligibilityCheck]
  )

  const rawText = useMemo(() => prettyJson(eligibilityCheck ?? rawBody), [eligibilityCheck, rawBody])

  const summaryRows = useMemo(() => {
    const inputs = mapped?.benefitInputs ?? {}
    const catalogKeys = new Set(BENEFIT_FIELDS.map((field) => field.key))
    const rows = BENEFIT_FIELDS.map((field) => ({
      key: field.key,
      label: field.label,
      value: inputs[field.key],
    }))
    for (const [key, value] of Object.entries(inputs)) {
      if (catalogKeys.has(key)) continue
      rows.push({ key, label: humanizeFieldKey(key), value })
    }
    return rows
  }, [mapped])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        setTab('summary')
        setCopied(false)
        onClose()
      }}
      title="Eligibility data"
      size="xl"
    >
      <p className="mb-4 text-sm text-gray-500">
        {patientName ? `${patientName} · ` : ''}
        Use the summary to see values the rules engine compares. Open raw JSON for the full eligibility
        response.
      </p>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex rounded-lg border border-gray-200 p-0.5">
          <button
            type="button"
            onClick={() => setTab('summary')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
              tab === 'summary' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Used for rules
          </button>
          <button
            type="button"
            onClick={() => setTab('raw')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
              tab === 'raw' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Raw JSON
          </button>
        </div>
        {tab === 'raw' ? (
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy JSON'}
          </button>
        ) : null}
      </div>

      {!eligibilityCheck ? (
        <p className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
          Eligibility check data is not available for this patient.
        </p>
      ) : tab === 'summary' ? (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Field
                </th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Patient eligibility value
                </th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((row) => (
                <tr key={row.key} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium text-gray-900">{row.label}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {row.value === undefined || row.value === '' ? '—' : String(row.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <pre className="max-h-[28rem] overflow-auto rounded-xl border border-gray-200 bg-gray-950 p-4 text-xs leading-5 text-gray-100">
          {rawText || 'No raw eligibility payload found.'}
        </pre>
      )}
    </Modal>
  )
}

export function EligibilityDataButton({
  onClick,
  disabled,
}: {
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <FileJson size={14} />
      View eligibility
    </button>
  )
}
